import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useQuery } from 'react-query';
import { CustomTooltip } from 'src/components/CustomTooltip';
import SubtitleLink from 'src/components/SubtitleLink';
import { getOffersForHome } from 'src/services/operation';
import { convertValue, toCurrency } from 'src/utils/number';

interface Offer {
  numeroRequerimento: number;
  dataRegistro?: string | null;
  valorMobiliario?: string | null;
  valorTotalRegistrado?: number | null;
}

interface OffersResponse {
  data: Offer[];
}

const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) return '—';

  return toCurrency(value);
};

const formatCompactCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) return '—';

  return convertValue(value, '', true, 'currency', 'BRL', 0, 0);
};

const formatDate = (value?: string | null): string =>
  value ? format(parseISO(value), 'dd/MM/yyyy') : '—';

const formatValue = (value?: string | null): string =>
  value && value !== '-' ? value : '—';

export default function Offers() {
  const {
    data: offers,
    isError,
    isLoading,
  } = useQuery<OffersResponse>('offersHome', getOffersForHome, {
    retry: false,
  });
  const rows = offers?.data ?? [];

  return (
    <Paper elevation={0} sx={{ p: '16px 16px 8px 24px' }}>
      <SubtitleLink
        title="Ofertas recentes"
        url="/operacoes?market=FIDC&mode=advanced&indicator=fidc-ofertas"
        labelLink="Ver todas as ofertas"
      />
      <Table sx={{ mt: 2 }} size="small" aria-label="Ofertas recentes">
        <TableHead>
          <TableRow>
            {['Mercado', 'Montante', 'Data'].map((header) => (
              <TableCell
                key={header}
                sx={{
                  color: '#797979',
                  fontSize: '14px',
                  fontWeight: 600,
                  px: 1,
                  py: 0.75,
                  whiteSpace: 'nowrap',
                }}>
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={3} sx={{ px: 1, py: 3, textAlign: 'center' }}>
                <CircularProgress size={20} aria-label="Carregando ofertas" />
              </TableCell>
            </TableRow>
          )}
          {isError && (
            <TableRow>
              <TableCell colSpan={3} sx={{ px: 1, py: 3, textAlign: 'center' }}>
                Não foi possível carregar as ofertas.
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} sx={{ px: 1, py: 3, textAlign: 'center' }}>
                Nenhuma oferta encontrada.
              </TableCell>
            </TableRow>
          )}
          {rows.map((offer) => (
            <TableRow key={offer.numeroRequerimento}>
              <TableCell
                sx={{
                  fontSize: '14px',
                  px: 1,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}>
                {formatValue(offer.valorMobiliario)}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  px: 1,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}>
                <CustomTooltip
                  title={formatCurrency(offer.valorTotalRegistrado)}>
                  <Box component="span">
                    {formatCompactCurrency(offer.valorTotalRegistrado)}
                  </Box>
                </CustomTooltip>
              </TableCell>
              <TableCell
                sx={{
                  fontSize: '14px',
                  px: 1,
                  py: 1,
                  whiteSpace: 'nowrap',
                }}>
                {formatDate(offer.dataRegistro)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
